import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Lock, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function ChangePasswordPage() {
  const { user, setMustChangePassword } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const forced = location.state?.forced || user?.must_change_password;

  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres");
      return;
    }
    if (form.next !== form.confirm) {
      toast.error("Las contraseñas no coinciden");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/change-password", {
        current_password: forced ? "ignored" : form.current,
        new_password: form.next,
      });
      toast.success("Contraseña actualizada");
      if (setMustChangePassword) setMustChangePassword(false);
      navigate("/");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al cambiar contraseña");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-6" data-testid="change-password-page">
      <Card className="w-full max-w-md rounded-none">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Lock size={24} className="text-[#002FA7]" />
            <CardTitle className="font-heading font-black text-2xl">
              {forced ? "Cambia tu contraseña" : "Cambiar contraseña"}
            </CardTitle>
          </div>
          {forced && (
            <div className="mt-3 p-3 bg-amber-50 border-l-2 border-amber-500 flex items-start gap-2">
              <Warning size={18} className="text-amber-700 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-900">
                Estás usando una contraseña temporal. Debes crear una nueva contraseña antes de continuar.
              </p>
            </div>
          )}
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {!forced && (
              <div>
                <Label className="text-xs font-mono uppercase">Contraseña actual</Label>
                <Input
                  type="password"
                  value={form.current}
                  onChange={(e) => setForm({ ...form, current: e.target.value })}
                  className="rounded-none mt-1"
                  required
                  data-testid="current-password"
                />
              </div>
            )}
            <div>
              <Label className="text-xs font-mono uppercase">Nueva contraseña</Label>
              <Input
                type="password"
                value={form.next}
                onChange={(e) => setForm({ ...form, next: e.target.value })}
                className="rounded-none mt-1"
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                data-testid="new-password"
              />
            </div>
            <div>
              <Label className="text-xs font-mono uppercase">Confirmar nueva contraseña</Label>
              <Input
                type="password"
                value={form.confirm}
                onChange={(e) => setForm({ ...form, confirm: e.target.value })}
                className="rounded-none mt-1"
                required
                data-testid="confirm-password"
              />
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-[#002FA7] hover:bg-[#001D66]"
              data-testid="submit-change-password"
            >
              {loading ? "Actualizando..." : "Cambiar contraseña"}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
